import React, { createContext, useCallback, useContext, useEffect,
  useMemo, useState } from "react";
import { api } from "@/lib/api";

const VaultContext = createContext({
  unlocked: false,
  configured: true,
  loading: true,
  refresh: () => {},
  unlock: async () => false,
  lock: async () => {},
});

/**
 * Holds the FE state for the Finance & Payroll vault. The backend is the
 * source of truth (httpOnly cookie + JWT); we just mirror status so the
 * UI can gate routes + render lock icons. On boot we fetch /vault/status
 * once; we re-check whenever a vaulted route receives a 423.
 */
export function VaultProvider({ children }) {
  const [state, setState] = useState({
    unlocked: false, configured: true, loading: true, expiresAt: null,
  });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/vault/status");
      setState({
        unlocked: !!data.unlocked,
        configured: data.configured !== false,
        loading: false,
        expiresAt: data.expires_at || null,
      });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Listen for 423 responses on vaulted endpoints — if our local state
  // says unlocked but the server disagrees, re-fetch to flip the UI.
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 423) {
          setState((s) => ({ ...s, unlocked: false }));
        }
        return Promise.reject(err);
      },
    );
    return () => api.interceptors.response.eject(interceptor);
  }, []);

  const unlock = useCallback(async (password) => {
    try {
      await api.post("/vault/unlock", { password });
      await refresh();
      return { ok: true };
    } catch (e) {
      return {
        ok: false,
        detail: e.response?.data?.detail || "Vault unlock failed",
      };
    }
  }, [refresh]);

  const lock = useCallback(async () => {
    try { await api.post("/vault/lock", {}); }
    catch { /* ignore */ }
    setState((s) => ({ ...s, unlocked: false, expiresAt: null }));
  }, []);

  const value = useMemo(() => ({
    ...state, refresh, unlock, lock,
  }), [state, refresh, unlock, lock]);

  return (
    <VaultContext.Provider value={value}>{children}</VaultContext.Provider>
  );
}

export function useVault() {
  return useContext(VaultContext);
}
